import { useEffect, useState } from "react";
import { api, filterParams } from "@/lib/api";
import { STANCE_COLORS, CHART_COLORS } from "@/lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const STANCES = ["Supportive", "Neutral", "Challenging", "Conflicting"];

const SharedLegend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="stance-legend">
    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400">HCP position</span>
    {STANCES.map((s) => (
      <span key={s} className="flex items-center gap-1.5 text-[11px] text-slate-600">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: STANCE_COLORS[s] }} />{s}
      </span>
    ))}
  </div>
);

const ChartCard = ({ kicker, title, children, testid }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid={testid}>
    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">{kicker}</p>
    <h4 className="text-sm font-semibold text-slate-900 mb-3 leading-snug">{title}</h4>
    {children}
  </div>
);

const StackedVertical = ({ data }) => (
  <ResponsiveContainer width="100%" height={230}>
    <BarChart data={data} margin={{ left: -20 }}>
      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} height={24} />
      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
      <Tooltip />
      {STANCES.map((s, idx) => (
        <Bar key={s} dataKey={s} stackId="a" fill={STANCE_COLORS[s]} radius={idx === 3 ? [4, 4, 0, 0] : 0} />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

const StackedHorizontal = ({ data, height = 230 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} layout="vertical" margin={{ left: 60 }}>
      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10.5 }} interval={0} />
      <Tooltip />
      {STANCES.map((s, idx) => (
        <Bar key={s} dataKey={s} stackId="a" fill={STANCE_COLORS[s]} radius={idx === 3 ? [0, 4, 4, 0] : 0} />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

export default function GeoSpecialty({ filters }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/breakdowns", { params: filterParams(filters) }).then((r) => setData(r.data));
  }, [filters]);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading breakdowns…</div>;

  return (
    <div className="space-y-4" data-testid="geo-view">
      <SharedLegend />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard kicker="HCP position by geography" title="North America shows the strongest supportive signal — and the highest challenge volume" testid="chart-geography">
          <StackedVertical data={data.by_geography} />
        </ChartCard>
        <ChartCard kicker="HCP position by specialty" title="Medical oncology dominates the voice base" testid="chart-specialty">
          <StackedHorizontal data={data.by_specialty} height={Math.max(230, data.by_specialty.length * 38)} />
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard kicker="Academic vs community setting" title="Academic centres anchor support; community voices carry the execution challenges" testid="chart-setting">
          <StackedVertical data={data.by_setting} />
        </ChartCard>
        <ChartCard kicker="Insights by treatment line" title="Second-line-plus decisions concentrate the conversation" testid="chart-treatment-line">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={data.by_treatment_line} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {data.by_treatment_line.map((e, i) => <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard kicker="Insights by HCP segment" title="Academic voices lead, but community perspectives are well represented" testid="chart-segment">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={data.by_segment} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {data.by_segment.map((e, i) => <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
